import React, { useEffect, useState, useMemo } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";
import InputNumberNoScroll from "../../../components/inputs/inputNumberNoScroll";

// Importamos el componente reutilizable
import EessAutocomplete from "../../../components/autocomplete/EessAutocomplete";

export default function EditarBonificacion({
  record,    // El registro que viene de la tabla (con ids y nombres)
  onClose,
  onUpdated,
}) {
  // --- 1. ESTADOS DE CAMPOS ---
  // Inicializamos con los datos del registro (record)
  const [nombre, setNombre] = useState(record?.nombre || "");
  const [cantidad, setCantidad] = useState(record?.cantidad || "");
  const [rubroId, setRubroId] = useState(record?.rubroId || "");
  
  // Para EESS necesitamos reconstruir el objeto {id, label} para el Autocomplete
  const [selectedEess, setSelectedEess] = useState(
    record?.eessId 
      ? { id: record.eessId, label: record.eessNombre } 
      : null
  );

  // --- 2. ESTADOS DE CATÁLOGOS ---
  const [listas, setListas] = useState({
    rubros: [],
  });

  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState("");

  // Validaciones
  const isValid =
    nombre.trim() !== "" &&
    cantidad !== "" &&
    rubroId !== ""; // EESS es opcional

  // Detectar cambios para habilitar botón guardar
  const changed = useMemo(() => {
    // Comparamos valor actual vs valor original (record)
    const currentEessId = selectedEess?.id || null;
    const originalEessId = record?.eessId || null;

    return (
      nombre !== record?.nombre ||
      Number(cantidad) !== Number(record?.cantidad) ||
      String(rubroId) !== String(record?.rubroId) ||
      currentEessId !== originalEessId
    );
  }, [nombre, cantidad, rubroId, selectedEess, record]);

  // Cerrar con ESC
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Cargar Rubros al iniciar
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const resRub = await appFetchJson(apiRoutes.rubros.listUrl({ page: 0, size: 100 }));
        setListas({
          rubros: resRub?.items || [],
        });
      } catch (e) {
        console.error(e);
        setError("Error al cargar los rubros.");
      } finally {
        setLoadingData(false);
      }
    };
    cargarDatos();
  }, []);

  const handleSave = async () => {
    if (!isValid || !changed || saving) return;
    setError("");
    setSaving(true);

    try {
      const payload = {
        id: record.id, // Importante para el PUT
        nombre: nombre.trim(),
        cantidad: Number(cantidad),
        rubroId: Number(rubroId),
        
        // Si hay seleccionada una EESS enviamos su ID, sino null
        eessId: selectedEess ? selectedEess.id : null, 
      };

      // Nota: Asegúrate que apiRoutes.bonificacion.update sea una función: (id) => string
      const url = apiRoutes.bonificacion.update 
        ? apiRoutes.bonificacion.update(record.id) 
        : `${apiRoutes.bonificacion.list}/${record.id}/editar`; // Fallback por si acaso

      await appFetchJson(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      onUpdated();
      onClose();
    } catch (e) {
      console.error(e);
      setError(e?.message || "Error al actualizar la bonificación.");
    } finally {
      setSaving(false);
    }
  };

  if (loadingData) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        
        {/* HEADER */}
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">Editar Bonificación</h2>
          <button className="modal-close-x" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* BODY */}
        <div className="modal-body">
          {error && <div className="text-red-600 mb-3 font-medium">{error}</div>}

          <div className="form-grid">
            
            {/* NOMBRE */}
            <div className="form-row">
              <label className="form-label">Descripción *</label>
              <input
                className="input"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
              />
            </div>

            {/* CANTIDAD */}
            <div className="form-row">
              <label className="form-label">Cantidad *</label>
              <InputNumberNoScroll
                className="input w-full h-[38px] bg-white border rounded px-3"
                value={cantidad}
                onChange={(e) => setCantidad(e.target.value)}
              />
            </div>

            {/* RUBRO */}
            <div className="form-row">
              <label className="form-label">Rubro *</label>
              <select
                className="input"
                value={rubroId}
                onChange={(e) => setRubroId(e.target.value)}
              >
                <option value="">--- Seleccione ---</option>
                {listas.rubros.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* EESS (Autocomplete) */}
            <div className="form-row">
              <label className="form-label">Estación (EE.SS.)</label>
              <div className="w-full">
                <EessAutocomplete
                  value={selectedEess}
                  onChange={setSelectedEess}
                  placeholder="Buscar Estación..."
                />
              </div>
            </div>

          </div>
        </div>

        {/* FOOTER */}
        <div className="modal-footer">
          <div className="btn-row">
            <BtnGuardar
              label="Guardar Cambios"
              isValid={isValid && changed}
              saving={saving}
              onClick={handleSave}
            />
            <BtnCancelar onClick={onClose} />
          </div>
        </div>
      </div>
    </div>
  );
}